// src/pages/InscripcionInstitucionPage.js
//
// Panel interno para que el SuperAdmin de Fenagro registre una nueva
// institución/alianza educativa en EducaT. YA NO ES AUTOSERVICIO -- el
// backend (POST /instituciones) exige rol Ecolab "SuperAdmin" desde el
// cambio de modelo de alianzas (ver InstitucionesController.Create).
// Cualquier otro usuario ve un mensaje explicando por qué no puede
// registrar instituciones directamente.

import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Container, Typography, Button, Grid, Paper, Chip, Alert } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import fenagroTheme from '../theme/fenagroTheme';
import Footer from '../components/layout/Footer';
import InstitucionInscripcionForm from '../components/InstitucionInscripcionForm';
import { useAuth } from '../context/AppContext';

const BENEFICIOS = [
  { icon: <AccountBalanceOutlinedIcon />, title: 'PEI propio',
    desc: 'La institución define su Programa Educativo Institucional, niveles, jornada y modelo pedagógico.' },
  { icon: <VideoCallOutlinedIcon />, title: 'Sesiones online y grabadas',
    desc: 'Clases en vivo vía Google Meet y videos grabados para sus estudiantes.' },
  { icon: <PaymentsOutlinedIcon />, title: 'Gestión de pensiones',
    desc: 'Tarifas por nivel, cuotas automáticas y control de cartera institucional.' },
  { icon: <GroupsOutlinedIcon />, title: 'Cursos de la alianza',
    desc: 'Estudiantes asociados a la cooperativa acceden a los cursos autorizados de la alianza.' },
];

const PASOS = [
  'Fenagro completa los datos generales y la ubicación de la institución.',
  'Fenagro registra a la persona que administrará el portal de esa institución.',
  'Esa persona recibe un correo para definir su contraseña e ingresar.',
  'Desde su portal, administra cursos, invita a su equipo (coordinadores, docentes) y gestiona a sus estudiantes.',
];

function AccesoRestringido() {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Paper elevation={0} sx={{ p: 5, maxWidth: 520, textAlign: 'center', borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <LockOutlinedIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Registro restringido
        </Typography>
        <Typography color="text.secondary">
          Registrar una institución o alianza educativa en {nombreProducto} es una acción exclusiva del
          SuperAdmin de Fenagro. Si tu institución quiere sumarse a la red, comunícate con el
          equipo de Fenagro para iniciar el proceso.
        </Typography>
      </Paper>
    </Box>
  );
}

function InscripcionContent() {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  const { isAuthenticated, isFenagroSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !isFenagroSuperAdmin()) {
    return <AccesoRestringido />;
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 0 }}>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff', py: 8, mb: 6 }}>
        <Container maxWidth="lg">
          <Chip label="PANEL SUPERADMIN — FENAGRO" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
            Registrar institución / alianza
          </Typography>
          <Typography sx={{ fontSize: '1.15rem', maxWidth: 780, mb: 4, color: 'rgba(255,255,255,0.9)' }}>
            Registra una institución educativa aliada de la red FENAGRO APL / Ecolab en {nombreProducto} y
            designa quién administrará su portal — cursos, estudiantes y pensiones de esa alianza.
          </Typography>
          <Button
            variant="contained" size="large" onClick={() => setOpen(true)}
            sx={{ bgcolor: '#f9a825', color: '#1a1a1a', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#f0d070' } }}
          >
            Registrar institución
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Beneficios */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a4d2e' }}>
          Qué obtiene la institución aliada
        </Typography>
        <Grid container spacing={2} sx={{ mb: 6 }}>
          {BENEFICIOS.map((b, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <Box sx={{ color: '#2e7d32', mb: 1.5, '& svg': { fontSize: 40 } }}>{b.icon}</Box>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{b.title}</Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>{b.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Cómo funciona */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a4d2e' }}>
          Cómo funciona
        </Typography>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0', mb: 6 }}>
          <Box component="ol" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
            {PASOS.map((p, i) => (
              <Box component="li" key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  bgcolor: '#2e7d32', color: '#fff', width: 30, height: 30, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ pt: 0.3 }}>{p}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* CTA final */}
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f7f5', borderRadius: 3, mb: 6 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a4d2e' }}>
            ¿Listo para registrar la alianza?
          </Typography>
          <Typography sx={{ color: '#555', mb: 3, maxWidth: 600, mx: 'auto' }}>
            El registro toma unos minutos. La institución y su administrador quedan activos de inmediato.
          </Typography>
          <Button variant="contained" size="large" onClick={() => setOpen(true)}
            sx={{ bgcolor: '#2e7d32', px: 4, '&:hover': { bgcolor: '#1a4d2e' } }}>
            Registrar institución
          </Button>
        </Box>
      </Container>

      <Footer />

      <InstitucionInscripcionForm
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={() => { /* el usuario ve la confirmación dentro del propio wizard */ }}
      />
    </Box>
  );
}

function InscripcionInstitucionPage() {
  return (
    <ThemeProvider theme={fenagroTheme}>
      <InscripcionContent />
    </ThemeProvider>
  );
}

export default InscripcionInstitucionPage;
